import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Preloader from '../components/Preloader';
import ScrollToTop from '../components/ScrollToTop';
import PageTitle from '../components/PageTitle';
import { useCart } from '../context/CartContext';
import { formatCurrencyShort } from '../utils/currency';

const rooms = [
  {
    id: 'r1',
    img: '/images/gw/rooms/room-standard.jpg',
    title: 'Greatwood Standard',
    price: 40000,
    tag: 'Standard',
    color: '#2d7a4f',
    type: 'room',
    desc: 'Beautifully appointed with a king-size bed, en-suite glass shower, air-conditioning, Smart TV and complimentary WiFi.',
    features: [
      'King Size Bed',
      'En-Suite Bathroom',
      'Air Conditioning',
      'Smart TV',
      'Free WiFi',
      '24h Power',
    ],
    persons: '1–2',
    size: '280 sq ft',
  },
  {
    id: 'r2',
    img: '/images/gw/rooms/room-executive.jpg',
    title: 'Greatwood Executive',
    price: 50000,
    tag: 'Executive',
    color: '#1565c0',
    type: 'room',
    desc: 'Generous space with premium furnishings, en-suite hot water bathroom, Smart TV and complimentary WiFi. Includes breakfast.',
    features: [
      'King Size Bed',
      'Premium En-Suite',
      'Air Conditioning',
      'Smart TV',
      'Free WiFi',
      'Complimentary Breakfast',
    ],
    persons: '1–2',
    size: '360 sq ft',
  },
  {
    id: 'r3',
    img: '/images/gw/rooms/room-special.jpg',
    title: 'Greatwood Special',
    price: 80000,
    tag: 'Special',
    color: '#7b1fa2',
    type: 'room',
    desc: 'Spacious room with a dedicated sitting area, king-size bed, premium bathroom and work desk. Perfect for extended stays.',
    features: [
      'King Size Bed',
      'Sitting Area',
      'Premium Bathroom',
      'Work Desk',
      'Air Conditioning',
      'Room Service',
    ],
    persons: '1–3',
    size: '500 sq ft',
  },
  {
    id: 'r4',
    img: '/images/gw/rooms/suite-sitting.jpg',
    title: 'Presidential Suite',
    price: 150000,
    tag: 'Presidential',
    color: '#b08d57',
    type: 'room',
    desc: 'The ultimate luxury — a grand sitting room with ornate furnishings, king-size bedroom, luxury bathroom and panoramic views.',
    features: [
      'King Size Bedroom',
      'Grand Sitting Room',
      'Luxury Bathroom',
      'Ornate Furnishings',
      'Dedicated Butler',
      'Room Service',
    ],
    persons: '1–4',
    size: '800 sq ft',
  },
];

function RoomCard({ room, onSelect }) {
  const { addToCart, cartItems } = useCart();
  const [flash, setFlash] = useState(false);
  const inCart = cartItems.some((i) => i.id === room.id);
  const handleAdd = () => {
    addToCart({ ...room, currency: '₦' });
    setFlash(true);
    setTimeout(() => setFlash(false), 1800);
  };

  return (
    <div className="page-room-card">
      <div className="page-room-img">
        <img src={room.img} alt={room.title} />
        <span className="page-room-tag" style={{ background: room.color }}>
          {room.tag}
        </span>
        <span className="page-room-price">
          ₦{formatCurrencyShort(room.price).replace('₦', '')}
          <small>/night</small>
        </span>
      </div>
      <div className="page-room-body">
        <h3>{room.title}</h3>
        <p>{room.desc}</p>
        <ul className="page-room-feats">
          {room.features.map((f, i) => (
            <li key={i}>
              <i className="fas fa-check"></i>
              {f}
            </li>
          ))}
        </ul>
        <div className="page-room-meta">
          <span>
            <i className="fas fa-user"></i>
            {room.persons}
          </span>
          <span>
            <i className="fas fa-ruler-combined"></i>
            {room.size}
          </span>
        </div>
        <div className="page-room-actions">
          <button
            className="page-room-detail-btn"
            onClick={() => onSelect(room)}
          >
            Details
          </button>
          <button
            className={
              'h2-book-btn' +
              (inCart ? ' incart' : '') +
              (flash ? ' added' : '')
            }
            onClick={handleAdd}
          >
            {flash ? '✓ Added' : inCart ? '✓ In Cart' : 'Book'}
          </button>
        </div>
      </div>
    </div>
  );
}

function RoomModal({ room, onClose }) {
  const { addToCart, cartItems } = useCart();
  const [flash, setFlash] = useState(false);
  const inCart = cartItems.some((i) => i.id === room.id);
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);
  const handleAdd = () => {
    addToCart({ ...room, currency: '₦' });
    setFlash(true);
    setTimeout(() => setFlash(false), 1800);
  };
  return (
    <div className="gw-modal-overlay" onClick={onClose}>
      <div className="gw-modal" onClick={(e) => e.stopPropagation()}>
        <button className="gw-modal-close" onClick={onClose}>
          <i className="fas fa-times"></i>
        </button>
        <div className="gw-modal-img">
          <img src={room.img} alt={room.title} />
          <span className="gw-room-tag" style={{ background: room.color }}>
            {room.tag}
          </span>
        </div>
        <div className="gw-modal-body">
          <div className="h2-label">{room.tag} Room</div>
          <h2
            style={{
              fontFamily: 'var(--gw-font-h,serif)',
              fontSize: 'clamp(22px,3vw,38px)',
              margin: '6px 0 8px',
              color: '#111',
            }}
          >
            {room.title}
          </h2>
          <div className="gw-modal-price">
            ₦{formatCurrencyShort(room.price).replace('₦', '')}
            <span>/night</span>
          </div>
          <p style={{ color: '#555', lineHeight: 1.8, marginBottom: 18 }}>
            {room.desc}
          </p>
          <div className="gw-modal-meta">
            <div>
              <i className="fas fa-user" style={{ color: '#b08d57' }}></i>
              <strong>Guests</strong>
              <span>{room.persons}</span>
            </div>
            <div>
              <i
                className="fas fa-ruler-combined"
                style={{ color: '#b08d57' }}
              ></i>
              <strong>Size</strong>
              <span>{room.size}</span>
            </div>
          </div>
          <ul className="gw-modal-features">
            {room.features.map((f, i) => (
              <li key={i}>
                <i className="fas fa-circle-check"></i>
                {f}
              </li>
            ))}
          </ul>
          <div className="gw-modal-actions">
            <button
              className={
                'gw-btn-book' +
                (inCart ? ' in-cart' : '') +
                (flash ? ' just-added' : '')
              }
              onClick={handleAdd}
              style={{ flex: 1 }}
            >
              {flash ? '✓ Added!' : inCart ? '✓ In Cart' : 'Book This Room'}
            </button>
            <Link to="/contact" className="gw-btn-contact" onClick={onClose}>
              <i className="fas fa-phone me-1"></i>Call to Reserve
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Rooms() {
  const { cartCount } = useCart();
  const [selected, setSelected] = useState(null);

  return (
    <div className="page-wrapper">
      <Preloader />
      <Header />
      <PageTitle title="Rooms & Tariff" breadcrumbs={[{ label: 'Rooms' }]} />

      <section style={{ padding: '60px 0' }}>
        <div className="auto-container">
          {/* Compact tariff table */}
          <div className="page-tariff-table">
            <div className="page-tariff-head">
              <img
                src="/images/gw/logo.png"
                alt="Greatwood Hotel"
                style={{ height: 48, objectFit: 'contain' }}
              />
              <div>
                <strong>Official Room Tariff</strong>
                <span>
                  Plot OF/12, Commercial District G, PH-Owerri Road (Opp. Duwez
                  Filling Station), Owerri, Imo State
                </span>
              </div>
            </div>
            <div className="page-tariff-row">
              {[
                { tag: 'Standard', price: '₦40,000', color: '#2d7a4f' },
                { tag: 'Executive', price: '₦50,000', color: '#1565c0' },
                { tag: 'Special', price: '₦80,000', color: '#7b1fa2' },
                { tag: 'Presidential', price: '₦150,000', color: '#b08d57' },
              ].map((t, i) => (
                <div
                  key={i}
                  className="page-tariff-cell"
                  style={{ borderTop: `3px solid ${t.color}` }}
                >
                  <span className="ptc-tag" style={{ color: t.color }}>
                    Greatwood {t.tag}
                  </span>
                  <span className="ptc-price">{t.price}</span>
                  <span className="ptc-per">per night</span>
                </div>
              ))}
            </div>
          </div>

          {/* Cart notice */}
          {cartCount > 0 && (
            <div className="cart-notice mb-4">
              <i className="fas fa-cart-shopping"></i>
              <span>
                <strong>{cartCount}</strong> item{cartCount > 1 ? 's' : ''} in
                cart
              </span>
              <Link to="/cart" className="cart-notice-link">
                View Cart →
              </Link>
            </div>
          )}

          {/* Room cards */}
          <div className="page-rooms-grid">
            {rooms.map((r) => (
              <RoomCard key={r.id} room={r} onSelect={setSelected} />
            ))}
          </div>

          {/* CTA */}
          <div className="page-rooms-cta">
            <div>
              <strong>Ready to book? Call us directly</strong>
              <span>Our team is available 24 hours a day</span>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <a href="tel:+2348076988183" className="gw-cta-phone">
                <i className="fas fa-phone"></i>0807 698 8183
              </a>
              <a
                href="https://wa.me/2348076988183"
                className="gw-cta-phone gw-cta-whatsapp"
              >
                <i className="fab fa-whatsapp"></i>WhatsApp
              </a>
            </div>
            <Link to="/contact" className="h2-btn-primary">
              Book Online
            </Link>
          </div>
        </div>
      </section>

      {selected && (
        <RoomModal room={selected} onClose={() => setSelected(null)} />
      )}
      <Footer />
      <ScrollToTop />
    </div>
  );
}
